// AVRASTRO data-viz primitives — built in-house (no chart library), following one method:
// the form follows the data's job; colour follows the entity (series tokens --s1..--s3 validated for
// CVD in both themes); thin marks, recessive grid, one y-axis, hover layer on every plot, a legend
// whenever there are two or more series, and a text alternative for every chart.
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

// ---------------------------------------------------------------------------------------------
// helpers
export function hashOf(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
/** Stable hue per person (identity, not rank). Skips the muddy yellow-green band. */
export function hueOf(s: string): number { const h = hashOf(s) % 300; return h < 60 ? h : h + 60; }

export function useWidth<T extends HTMLElement>(fallback = 640): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setW(Math.max(200, Math.round(el.getBoundingClientRect().width)));
    set();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0];
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  if (out[out.length - 1]! < max) out.push(out[out.length - 1]! + step);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Dial — a single headline number (0–100) with its band. Form: hero number + ring, not a chart.
export function Dial({ score, size = 112, stroke = 10, label, band, testId }: { score: number; size?: number; stroke?: number; label?: ReactNode; band: string; testId?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const sweep = 0.78; // 280°
  const [shown, setShown] = useState(0);
  useEffect(() => { const id = requestAnimationFrame(() => setShown(score)); return () => cancelAnimationFrame(id); }, [score]);
  const color = BAND_COLOR[band] ?? 'var(--s1)';
  return (
    <div className={`dial${size < 90 ? ' sm' : ''}`} style={{ width: size, height: size }} data-testid={testId} role="img" aria-label={`${score}/100`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(129deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--grid)" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${c * sweep} ${c}`} />
        <circle className="dial-arc" cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${c * sweep} ${c}`} strokeDashoffset={c * sweep * (1 - Math.max(0, Math.min(100, shown)) / 100)} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
      </svg>
      <div className="dial-val"><b>{score}</b>{label && <span>{label}</span>}</div>
    </div>
  );
}
export const BAND_COLOR: Record<string, string> = { strong: 'var(--st-good)', steady: 'var(--s1)', watch: 'var(--st-warn)', at_risk: 'var(--st-crit)' };

export function Band({ band, label }: { band: string; label: string }) {
  return <span className={`band ${band}`}><i />{label}</span>;
}

export function FactorBars({ rows }: { rows: { key: string; label: string; value: number; max: number; hint?: string }[] }) {
  return (
    <div className="factors">
      {rows.map((f) => (
        <div key={f.key} className="factor" title={f.hint}>
          <span className="muted one-line">{f.label}</span>
          <span className="track"><i style={{ width: `${(f.value / Math.max(1, f.max)) * 100}%` }} /></span>
          <b>{f.value}<span className="muted" style={{ fontWeight: 400 }}>/{f.max}</span></b>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Coverage radar — six fixed axes, two series (need vs held), legend + side list as text view.
export interface RadarArea { key: string; label: string; need: number; held: number; gap: boolean }
export function Radar({ areas, active, onActive, needLabel, heldLabel }: { areas: RadarArea[]; active?: string | null; onActive?: (k: string | null) => void; needLabel: string; heldLabel: string }) {
  const S = 360, C = S / 2, R = 108;
  const n = areas.length;
  const pt = (i: number, v: number) => { const a = -Math.PI / 2 + (i * 2 * Math.PI) / n; return [C + Math.cos(a) * R * v, C + Math.sin(a) * R * v] as const; };
  const poly = (vals: number[]) => vals.map((v, i) => pt(i, v).join(',')).join(' ');
  const need = areas.map((a) => Math.max(0.06, a.need / 3));
  const held = areas.map((a) => Math.max(0.04, a.held));
  return (
    <div className="radar">
      <svg viewBox={`0 0 ${S} ${S}`} role="img" aria-label={`${needLabel} / ${heldLabel}`}>
        {[1 / 3, 2 / 3, 1].map((lv) => <polygon key={lv} points={poly(areas.map(() => lv))} fill="none" stroke="var(--grid)" strokeWidth={1} />)}
        {areas.map((_, i) => { const [x, y] = pt(i, 1); return <line key={i} x1={C} y1={C} x2={x} y2={y} stroke="var(--grid)" strokeWidth={1} />; })}
        <polygon points={poly(need)} fill="var(--s-need)" fillOpacity={0.1} stroke="var(--s-need)" strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />
        <polygon points={poly(held)} fill="var(--s3)" fillOpacity={0.22} stroke="var(--s3)" strokeWidth={2} strokeLinejoin="round" style={{ transition: 'all .6s var(--ease)' }} />
        {areas.map((a, i) => {
          const [hx, hy] = pt(i, held[i]!);
          const [lx, ly] = pt(i, 1.24);
          const [ax, ay] = pt(i, 1);
          const on = active === a.key;
          return (
            <g key={a.key} className="axis-hit" onMouseEnter={() => onActive?.(a.key)} onMouseLeave={() => onActive?.(null)} onFocus={() => onActive?.(a.key)} onBlur={() => onActive?.(null)} tabIndex={0}>
              <line x1={C} y1={C} x2={ax} y2={ay} stroke="transparent" strokeWidth={26} />
              <circle cx={hx} cy={hy} r={on ? 6 : 4.5} fill="var(--s3)" stroke="var(--panel-solid)" strokeWidth={2} />
              {a.gap && <circle cx={ax} cy={ay} r={5} fill="var(--st-crit)" stroke="var(--panel-solid)" strokeWidth={2} />}
              <text x={lx} y={ly} textAnchor={Math.abs(lx - C) < 8 ? 'middle' : lx > C ? 'start' : 'end'} dominantBaseline="middle" style={{ fill: on ? 'var(--text)' : a.gap ? 'var(--crit)' : 'var(--text-2)', fontSize: 12, fontWeight: on || a.gap ? 700 : 550 }}>{a.label}</text>
            </g>
          );
        })}
      </svg>
      <div className="legend" style={{ justifyContent: 'center', marginTop: 4 }}>
        <span><i className="dash" style={{ color: 'var(--s-need)' }} />{needLabel}</span>
        <span><i style={{ background: 'var(--s3)' }} />{heldLabel}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Line chart with crosshair tooltip (comparator projection). One y-axis; solid = value, dashed = paid.
export interface LineSeries { id: string; label: string; color: string; values: number[]; dashed?: boolean; endLabel?: string }
export function LineChart({ series, xLabel, format, height = 280, ariaLabel }: { series: LineSeries[]; xLabel: (i: number) => string; format: (n: number) => string; height?: number; ariaLabel: string }) {
  const [ref, W] = useWidth<HTMLDivElement>(720);
  const [hover, setHover] = useState<number | null>(null);
  const n = Math.max(0, ...series.map((s) => s.values.length));
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const ticks = niceTicks(max, 4);
  const top = ticks[ticks.length - 1]!;
  const padL = 64, padR = 44, padT = 12, padB = 28;
  const iw = Math.max(10, W - padL - padR), ih = height - padT - padB;
  const x = (i: number) => padL + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v: number) => padT + ih - (v / top) * ih;
  const path = (vals: number[]) => vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 70))));
  const onMove = (e: PointerEvent) => {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    if (n < 1) return;
    const i = Math.round(((px - padL) / iw) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  // direct labels at line ends, nudged apart so they never collide
  const ends = series.filter((s) => s.endLabel && s.values.length).map((s) => ({ s, y: y(s.values[s.values.length - 1]!) })).sort((a, b) => a.y - b.y);
  for (let i = 1; i < ends.length; i++) if (ends[i]!.y - ends[i - 1]!.y < 14) ends[i]!.y = ends[i - 1]!.y + 14;
  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} role="img" aria-label={ariaLabel} onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {ticks.map((tv) => <g key={tv}><line x1={padL} x2={padL + iw} y1={y(tv)} y2={y(tv)} stroke="var(--grid)" strokeWidth={1} /><text x={padL - 8} y={y(tv)} textAnchor="end" dominantBaseline="middle">{format(tv)}</text></g>)}
        <line x1={padL} x2={padL + iw} y1={y(0)} y2={y(0)} stroke="var(--axis)" strokeWidth={1} />
        {Array.from({ length: n }, (_, i) => i).filter((i) => i % every === every - 1 || i === 0).map((i) => <text key={i} x={x(i)} y={height - 8} textAnchor="middle">{xLabel(i)}</text>)}
        {series.map((s) => <path key={s.id} d={path(s.values)} fill="none" stroke={s.color} strokeWidth={s.dashed ? 1.5 : 2} strokeDasharray={s.dashed ? '5 4' : undefined} strokeLinejoin="round" strokeLinecap="round" opacity={s.dashed ? 0.8 : 1} />)}
        {ends.map(({ s, y: ly }) => <text key={s.id} className="lbl" x={padL + iw + 8} y={ly} dominantBaseline="middle" style={{ fill: 'var(--text-2)' }}>{s.endLabel}</text>)}
        {hover !== null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + ih} stroke="var(--axis)" strokeWidth={1} />
            {series.map((s) => s.values[hover] !== undefined && <circle key={s.id} cx={x(hover)} cy={y(s.values[hover]!)} r={s.dashed ? 3 : 4.5} fill={s.color} stroke="var(--panel-solid)" strokeWidth={2} />)}
          </g>
        )}
        <rect x={padL} y={padT} width={iw} height={ih} fill="transparent" />
      </svg>
      {hover !== null && (
        <div className="tip" style={{ left: `${(x(hover) / W) * 100}%`, top: '40%' }}>
          <b>{xLabel(hover)}</b>
          {series.map((s) => s.values[hover] !== undefined && (
            <div key={s.id} className="r"><i style={{ background: s.color, opacity: s.dashed ? 0.6 : 1 }} /><span className="muted">{s.label}</span><b className="num">{format(s.values[hover]!)}</b></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Constellation — the book as a living map. Radius = urgency (centre = now), size = √portfolio,
// angle = stable hash of the person, colour = urgency status (always paired with the legend + list).
export interface Star { id: string; name: string; tier: 'now' | 'soon' | 'calm'; value: number; reason?: string; sub?: string }
const TIER_R = { now: [0.2, 0.5], soon: [0.58, 0.76], calm: [0.83, 0.98] } as const;
const TIER_COLOR = { now: '--st-crit', soon: '--st-warn', calm: '--s1' } as const;

export function Constellation({ stars, height = 360, onOpen, youLabel, labelsFor = 6 }: { stars: Star[]; height?: number; onOpen: (id: string) => void; youLabel: string; labelsFor?: number }) {
  const wrap = useRef<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [tip, setTip] = useState<{ s: Star; x: number; y: number } | null>(null);
  const hoverRef = useRef<string | null>(null);
  const maxV = Math.max(1, ...stars.map((s) => s.value));
  // layout in unit space, relaxed once so stars in a ring don't overlap
  const layout = useMemo(() => {
    const pts = stars.map((s) => {
      const h = hashOf(s.id);
      const [r0, r1] = TIER_R[s.tier];
      const rr = r0 + ((h >>> 8) % 1000) / 1000 * (r1 - r0);
      const a = ((h % 3600) / 3600) * Math.PI * 2;
      return { s, a, rr, size: 3.2 + Math.sqrt(s.value / maxV) * 11 };
    });
    for (let it = 0; it < 40; it++) {
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const p = pts[i]!, q = pts[j]!;
        const dx = Math.cos(p.a) * p.rr - Math.cos(q.a) * q.rr, dy = Math.sin(p.a) * p.rr - Math.sin(q.a) * q.rr;
        const d = Math.hypot(dx, dy), min = (p.size + q.size + 6) / 320;
        if (d < min) { const push = (min - d) / 2 / Math.max(0.05, p.rr); p.a += push; q.a -= push; }
      }
    }
    return pts;
  }, [stars, maxV]);

  useEffect(() => {
    const cv = canvas.current, el = wrap.current;
    if (!cv || !el) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0, raf = 0, colors: Record<string, string> = {}, t0 = performance.now(), last = 0;
    const readColors = () => {
      const cs = getComputedStyle(document.documentElement);
      colors = Object.fromEntries(['--st-crit', '--st-warn', '--s1', '--gold', '--gold-2', '--grid', '--line-2', '--text', '--text-2', '--panel-solid', '--muted'].map((k) => [k, cs.getPropertyValue(k).trim() || '#888']));
    };
    const resize = () => {
      const r = el.getBoundingClientRect();
      W = r.width; H = height;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
      cv.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      readColors();
      draw(performance.now());
    };
    // never negative: a hidden or very narrow panel (width 0) made ellipse() throw and the whole «Now» screen fail
    const ry = () => Math.max(0, H / 2 - 16);
    const rx = () => Math.max(0, W / 2 - 18);
    const place = (p: typeof layout[number], time: number) => {
      const drift = reduce ? 0 : ((time - t0) / 1000) * (p.s.tier === 'now' ? 0.012 : p.s.tier === 'soon' ? 0.007 : 0.004);
      const a = p.a + drift;
      return [W / 2 + Math.cos(a) * p.rr * rx(), H / 2 + Math.sin(a) * p.rr * ry()] as const;
    };
    function draw(time: number) {
      ctx!.clearRect(0, 0, W, H);
      // orbits
      ctx!.setLineDash([2, 6]);
      ctx!.strokeStyle = colors['--line-2']!;
      ctx!.lineWidth = 1;
      for (const [, r1] of Object.values(TIER_R)) { ctx!.beginPath(); ctx!.ellipse(W / 2, H / 2, r1 * rx(), r1 * ry(), 0, 0, Math.PI * 2); ctx!.stroke(); }
      ctx!.setLineDash([]);
      // you
      const g = ctx!.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, 38);
      g.addColorStop(0, colors['--gold-2']!); g.addColorStop(0.35, colors['--gold']!); g.addColorStop(1, 'transparent');
      ctx!.globalAlpha = 0.5; ctx!.fillStyle = g; ctx!.beginPath(); ctx!.arc(W / 2, H / 2, 38, 0, Math.PI * 2); ctx!.fill(); ctx!.globalAlpha = 1;
      ctx!.fillStyle = colors['--gold-2']!; ctx!.beginPath(); ctx!.arc(W / 2, H / 2, 6, 0, Math.PI * 2); ctx!.fill();
      ctx!.font = '600 11px system-ui, sans-serif'; ctx!.textAlign = 'center'; ctx!.fillStyle = colors['--text-2']!;
      ctx!.fillText(youLabel, W / 2, H / 2 + 22);
      // stars (calm first so urgent ones sit on top)
      const order = [...layout].sort((a, b) => (a.s.tier === b.s.tier ? 0 : a.s.tier === 'calm' ? -1 : b.s.tier === 'calm' ? 1 : a.s.tier === 'soon' ? -1 : 1));
      let labelled = 0;
      for (const p of order) {
        const [x, y] = place(p, time);
        const c = colors[TIER_COLOR[p.s.tier]]!;
        const hov = hoverRef.current === p.s.id;
        if (p.s.tier === 'now') {
          const pulse = reduce ? 0.5 : (Math.sin(time / 520 + (hashOf(p.s.id) % 10)) + 1) / 2;
          ctx!.globalAlpha = 0.18 + pulse * 0.18; ctx!.fillStyle = c; ctx!.beginPath(); ctx!.arc(x, y, p.size + 5 + pulse * 4, 0, Math.PI * 2); ctx!.fill(); ctx!.globalAlpha = 1;
        }
        ctx!.fillStyle = c; ctx!.globalAlpha = p.s.tier === 'calm' ? 0.75 : 1;
        ctx!.beginPath(); ctx!.arc(x, y, p.size + (hov ? 2 : 0), 0, Math.PI * 2); ctx!.fill(); ctx!.globalAlpha = 1;
        ctx!.lineWidth = 2; ctx!.strokeStyle = colors['--panel-solid']!; ctx!.stroke();
        if (hov) { ctx!.lineWidth = 2; ctx!.strokeStyle = colors['--text']!; ctx!.beginPath(); ctx!.arc(x, y, p.size + 5, 0, Math.PI * 2); ctx!.stroke(); }
      }
      // selective direct labels: the most urgent / most valuable few
      ctx!.textAlign = 'left'; ctx!.font = '600 11.5px system-ui, sans-serif';
      for (const p of [...layout].filter((p) => p.s.tier === 'now').sort((a, b) => b.s.value - a.s.value)) {
        if (labelled >= labelsFor) break;
        const [x, y] = place(p, time);
        const first = p.s.name.split(' ')[0] ?? p.s.name;
        ctx!.fillStyle = colors['--text']!; ctx!.fillText(first, x + p.size + 6, y + 4);
        labelled++;
      }
    }
    const loop = (time: number) => { if (time - last > 32) { draw(time); last = time; } raf = requestAnimationFrame(loop); };
    const hit = (e: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const time = performance.now();
      let best: { p: typeof layout[number]; d: number; x: number; y: number } | null = null;
      for (const p of layout) { const [x, y] = place(p, time); const d = Math.hypot(x - mx, y - my); if (d <= p.size + 7 && (!best || d < best.d)) best = { p, d, x, y }; }
      return best;
    };
    const onMove = (e: PointerEvent) => {
      const b = hit(e);
      hoverRef.current = b?.p.s.id ?? null;
      cv.style.cursor = b ? 'pointer' : 'default';
      setTip(b ? { s: b.p.s, x: b.x, y: b.y } : null);
      if (reduce) draw(performance.now());
    };
    const onLeave = () => { hoverRef.current = null; setTip(null); if (reduce) draw(performance.now()); };
    const onClick = (e: PointerEvent) => { const b = hit(e); if (b) onOpen(b.p.s.id); };
    resize();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(el);
    const mo = new MutationObserver(() => { readColors(); draw(performance.now()); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    cv.addEventListener('pointermove', onMove); cv.addEventListener('pointerleave', onLeave); cv.addEventListener('click', onClick);
    if (!reduce) raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); mo.disconnect(); cv.removeEventListener('pointermove', onMove); cv.removeEventListener('pointerleave', onLeave); cv.removeEventListener('click', onClick); };
  }, [layout, height, onOpen, youLabel, labelsFor]);

  const sorted = [...stars].sort((a, b) => (a.tier === b.tier ? b.value - a.value : a.tier === 'now' ? -1 : b.tier === 'now' ? 1 : a.tier === 'soon' ? -1 : 1));
  return (
    <div className="constellation" ref={wrap} style={{ height }} data-testid="constellation">
      <canvas ref={canvas} aria-hidden="true" />
      {tip && (
        <div className="tip" style={{ left: tip.x, top: tip.y }}>
          <b className="person-name">{tip.s.name}</b>
          {tip.s.reason && <div>{tip.s.reason}</div>}
          {tip.s.sub && <div className="muted tiny" style={{ marginTop: 4 }}>{tip.s.sub}</div>}
        </div>
      )}
      <ul className="sr-only">{sorted.map((s) => <li key={s.id}><button onClick={() => onOpen(s.id)}>{s.name}{s.reason ? ` — ${s.reason}` : ''}</button></li>)}</ul>
    </div>
  );
}
export const TIER_VAR = { now: 'var(--st-crit)', soon: 'var(--st-warn)', calm: 'var(--s1)' } as const;
